import { zodResolver } from "@hookform/resolvers/zod";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Loading, Panel, Recaptcha, Typography } from "@/components/atoms";
import { AccountType } from "@/constants/accountTypes";
import { getApiHostUrl } from "@/helpers/apiHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useRegisterClientMutation } from "@/redux/apis/auth/authApi";
import { useUpdateProfileMutation } from "@/redux/apis/profile/profileApi";
import { addToast } from "@/utils";
import { ContactNumberField, HowDidYouHearAboutUsField, JobTitleField, CompanyField } from "../SignUp/forms/fields";

// Schema without first name, last name, email, password fields
const clientRegistrationSchema = z.object({
  company: z.string().optional(),
  jobTitle: z.string().optional(),
  contactNumber: z.string().min(1, "Contact number is required"),
  howDidYouHearAboutUs: z.string().optional(),
});

type ClientRegistrationFormData = z.infer<typeof clientRegistrationSchema>;

interface ClientRegistrationStepProps {
  onRegistrationComplete: () => void;
  providerCode: string;
  userType: AccountType;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
}

export const ClientRegistrationStep = ({
  onRegistrationComplete,
  providerCode,
  userType,
  userEmail,
  userFirstName,
  userLastName,
}: ClientRegistrationStepProps) => {
  const operator = useOperator();
  const [registerClient] = useRegisterClientMutation();
  const [updateProfile] = useUpdateProfileMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const methods = useForm<ClientRegistrationFormData>({
    resolver: zodResolver(clientRegistrationSchema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const onRecaptchaChange = (token: string | null) => setRecaptchaToken(token);

  const onSubmit = handleSubmit(
    async (data) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        // Register the client with the API
        await registerClient({
          apiUrl: getApiHostUrl(providerCode),
          first_name: userFirstName,
          last_name: userLastName,
          company_name: data.company,
          job_title: data.jobTitle,
          email: userEmail,
          mobile: data.contactNumber,
          hear_about_us: data.howDidYouHearAboutUs || "kinde_registration",
          provider_code: providerCode,
          type: userType,
          registered_device: "web-app",
        }).unwrap();

        // Mark registration as complete in profile metadata
        await updateProfile({
          account_metadata: {
            is_register_complete: true,
            provider_code: providerCode,
            type: userType,
          },
        }).unwrap();

        addToast("success", "Welcome! Your account is ready");
        onRegistrationComplete();
      } catch (error) {
        console.error("Error completing client registration:", error);
        getErrorMessages(error as FetchBaseQueryError | SerializedError).forEach((m) => addToast("danger", m));
        setIsSubmitting(false);
      }
    },
    (errors) => {
      Object.values(errors).forEach((error) => {
        if (error?.message) {
          addToast("danger", error.message);
        }
      });
    }
  );

  return (
    <Panel className="relative w-full max-w-[680px] p-8">
      {isSubmitting && <Loading className="rounded-lg" />}
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <div className="mb-7 flex flex-row items-center">
            <div className="flex flex-1 flex-col gap-y-1.5">
              <Typography variant="h1">{`Complete your registration`}</Typography>
              <Typography variant="paragraph">{`Finish setting up your account with ${operator.name}`}</Typography>
            </div>
            {operator.images?.emblem && (
              <img src={operator.images.emblem} alt={`${operator.name} emblem`} className="h-16 w-16 object-contain" />
            )}
          </div>

          <div className="grid animate-enter grid-cols-2 gap-x-2.5 gap-y-3 lg:gap-x-5 lg:gap-y-6">
            <CompanyField className="col-span-2 lg:col-span-1" />
            <JobTitleField className="col-span-2 lg:col-span-1" />
            <ContactNumberField className="col-span-2 lg:col-span-1" />
            <HowDidYouHearAboutUsField className="col-span-2 lg:col-span-1" />
            <Recaptcha onChange={onRecaptchaChange} className="col-span-2" />
          </div>

          <Button variant="primary" type="submit" size="lg" className="mt-8 w-full" disabled={!isDirty || isSubmitting || !recaptchaToken}>
            Complete Registration
          </Button>
        </form>
      </FormProvider>
    </Panel>
  );
};
